import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  BULK_PDF_EXPORT_LIMIT,
  FormElement,
  FormElementKind,
  GetInspectionsRequestDTO,
  InspectionDTO,
  InspectionOrderByFieldEnumSchema,
  MODULE,
  PdfScope,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import { DeleteIcon } from '@/components/icons/delete';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import LinkViewIcon from '@/components/icons/link-view';
import PencilIcon from '@/components/icons/pencil';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { DownloadCSVIcon } from '@/legacy/components/icons/download-csv';
import ShareIcon from '@/legacy/components/icons/share';
import { buildFilterFields } from '@/queries/column-filter';
import { getFormByIdQueryOptions } from '@/queries/forms';
import { getInspectionsInfiniteQueryOptions, inspectionKeys } from '@/queries/inspections';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { exportRecordsToPdfZip } from '@/utils/bulk-pdf-export';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';
import { useEditFormNavigate } from '@/utils/routes';

import { InspectionsTable, NoInspectionsFound, NoInspectionsFoundFromFiltersDisplay, NoInspectionsFoundFromSearchDisplay } from './table';
import { columns, FILTER_FIELD_MAP, getElementIdFromColumnId, groupOptions, isFormFieldColumn } from './table-meta';
import { TopbarActions } from './topbar-actions';

function InspectionSubmissionsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const { org } = useUserStore();
  const handleEditInspectionForm = useEditFormNavigate();
  const { canManageInspectionForms, canEditInspectionRecord, canExportCsv } = usePermissions();

  const { id: formId } = useParams();

  const { data: formData } = useQuery(getFormByIdQueryOptions({ formId }));

  // Extract form elements from the form
  const formElements = useMemo<FormElement[]>(() => {
    return (formData?.form?.elements as FormElement[]) ?? [];
  }, [formData]);

  // Fetch units for unit reading columns
  const { data: units } = useQuery(getUnitsQueryOptions());

  // Create dynamic columns with form elements and units
  const dynamicColumns = useMemo(() => columns(formElements, units), [formElements, units]);

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.InspectionSubmissions, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: dynamicColumns,
  });

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isDownloadingPdfs, setIsDownloadingPdfs] = useState(false);

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  // Build formFields filter array for the API from form field column filters
  const formFieldFilters = useMemo(() => {
    if (!filters || !formElements.length) return undefined;

    const formFieldFilterList: { elementId: string; kind: FormElementKind; value: string | string[] | { from: string; to: string } }[] = [];

    for (const f of filters) {
      if (!isFormFieldColumn(f.id)) continue;

      const elementId = getElementIdFromColumnId(f.id);
      const element = formElements.find((e) => e.id === elementId);
      if (!elementId || !element || f.value === undefined || f.value === null || (Array.isArray(f.value) && f.value.length === 0)) continue;

      formFieldFilterList.push({
        elementId,
        kind: element.kind,
        value: f.value as string | string[] | { from: string; to: string },
      });
    }

    return formFieldFilterList.length > 0 ? formFieldFilterList : undefined;
  }, [filters, formElements]);

  // Build orderBy object, handling form field sorting specially
  const orderBy = useMemo(() => {
    if (!sorting.key) return undefined;

    if (isFormFieldColumn(sorting.key)) {
      const elementId = getElementIdFromColumnId(sorting.key);
      const element = formElements.find((e) => e.id === elementId);
      if (!elementId || !element) return undefined;

      return {
        field: 'formField' as const,
        direction: sorting.direction as 'asc' | 'desc',
        formField: { elementId, kind: element.kind },
      };
    }

    return {
      field: sorting.key as z.infer<typeof InspectionOrderByFieldEnumSchema>,
      direction: sorting.direction as 'asc' | 'desc',
    };
  }, [sorting.key, sorting.direction, formElements]);

  const {
    data: inspectionsData,
    fetchNextPage: fetchNextInspectionsPage,
    hasNextPage: hasNextInspectionsPage,
    isFetching: isFetchingInspections,
    isFetchingNextPage: isFetchingNextInspectionsPage,
    error: inspectionsError,
  } = useInfiniteQuery(
    getInspectionsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          extraFields: { formId: [formId as string], formFields: formFieldFilters },
          dropUndefined: true,
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetInspectionsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy,
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextInspectionsPage,
    hasNextPage: hasNextInspectionsPage,
    isFetching: isFetchingInspections,
    isError: !!inspectionsError,
    root: scrollRoot,
  });

  useEffect(() => {
    if (inspectionsError) {
      toast.error(inspectionsError.message);
    }
  }, [toast, inspectionsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => inspectionsData?.pages.flatMap((page) => page.data) ?? [], [inspectionsData]);

  const { mutate: deleteInspections, isPending: isDeletingInspections } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteInspections({ inspectionIds: ids }),
    onSuccess: () => {
      toast.success('Inspections deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: inspectionKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const getSelectedInspections = useMemo(() => {
    return (incomingData: typeof inspectionsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const inspections = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => inspections[parseInt(id)]);
    };
  }, [selectedRows]);

  function handleDeleteInspections() {
    if (inspectionsData?.pages) {
      const toDelete = getSelectedInspections(inspectionsData);
      deleteInspections(toDelete.map((inspection) => inspection.id));
    }
  }

  const selectedInspections = useMemo(() => getSelectedInspections(inspectionsData), [inspectionsData, getSelectedInspections]);
  // Members see Delete only when every selected record is theirs; Admins always see it.
  const canBulkActOnSelectedInspections =
    selectedInspections.length > 0 && selectedInspections.every((inspection) => canEditInspectionRecord(inspection.inspectedById));

  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  function buildInspectionsExportRequest(): RegisterExportQuery<'inspections'> {
    const selected = getSelectedInspections(inspectionsData);
    return {
      queryType: 'inspections',
      filters: { fields: { id: selected.map((inspection) => inspection.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetInspectionsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!inspectionsData?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildInspectionsExportRequest(), filename: buildCsvExportFilename('inspections') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!inspectionsData?.pages) return;
    const records = getSelectedInspections(inspectionsData);
    if ((scope === 'records' || scope === 'register-and-records') && records.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(buildPdfExportJob({ scope, recordType: 'inspection', records, register: buildInspectionsExportRequest() }));
  }

  async function handlePdfExportZip() {
    if (!inspectionsData?.pages) return;
    const selected = getSelectedInspections(inspectionsData);
    setIsDownloadingPdfs(true);
    try {
      await exportRecordsToPdfZip(selected, 'inspections');
      toast.success('PDF export completed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export PDFs');
    } finally {
      setIsDownloadingPdfs(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedInspections.length && (
              <div className="flex h-full items-center gap-x-2 px-2">
                <TablePageTitle>{formData?.form.name ?? ''}</TablePageTitle>
                <TablePageSearchBar search={search} setSearch={setSearch} />
                {filters && filters.length > 0 && (
                  <TablePageHeaderActionButton
                    icon={<UndoIcon />}
                    label="Remove all Filters"
                    kind="secondary"
                    onClick={() => setModuleFilters(MODULE.InspectionSubmissions, [])}
                  />
                )}
              </div>
            )}

            {selectedInspections.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedInspections.length} totalCount={inspectionsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCSVIcon />} label="Download CSV" onClick={handleCsvExport} />}
                {isRegisterPdfExportEnabled ? (
                  <DownloadPdfMenu recordNoun="inspection" onSelect={handlePdfScopeSelect} canExportRegister={canExportCsv} />
                ) : (
                  <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" onClick={handlePdfExportZip} disabled={isDownloadingPdfs} />
                )}
                {canBulkActOnSelectedInspections && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    label="Delete Selected"
                    onClick={() => setOpenDeleteConfirmation(true)}
                  />
                )}
              </div>
            )}

            <div className="mr-1 flex h-full items-center">
              <div className="flex items-center">
                {!selectedInspections.length && (
                  <>
                    {formData?.form?.id && canManageInspectionForms && (
                      <TablePageHeaderActionButton icon={<PencilIcon />} label="Edit Form" onClick={() => handleEditInspectionForm(formData?.form?.id)} />
                    )}
                  </>
                )}
                <TablePageTableGroupDropdown
                  columns={groupOptions}
                  value={grouping.length ? grouping[0] : ''}
                  onChange={(value) =>
                    updatePageConfig({
                      table: { grouping: value ? [value] : [] },
                    })
                  }
                />
                <ColumnsDialog<InspectionDTO>
                  open={openColumnsDialog}
                  onOpenChange={setOpenColumnsDialog}
                  columns={dynamicColumns}
                  columnOrder={columnOrder}
                  onColumnOrderChange={(columnOrder) =>
                    updatePageConfig({
                      table: { columnOrder: columnOrder as Array<{ id: string; active: boolean }> },
                    })
                  }
                />
                <TablePageHeaderActionButton
                  onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}/inspection`)}
                  icon={<ShareIcon />}
                  label="Share Link"
                />
                <TablePageHeaderActionButton
                  onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}/inspection`, '_blank')}
                  icon={<LinkViewIcon />}
                  label="Open Operator Web App"
                />
              </div>
            </div>
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <InspectionsTable
          data={flattenedData}
          loading={(isFetchingInspections && !isFetchingNextInspectionsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoInspectionsFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoInspectionsFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.InspectionSubmissions, [])} />
            ) : (
              <NoInspectionsFound />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.InspectionSubmissions, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
          columns={dynamicColumns}
        />
        {hasNextInspectionsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextInspectionsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteInspections}
        loading={isDeletingInspections}
        title="Delete inspections?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these inspections?"
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}
    </div>
  );
}

export default InspectionSubmissionsPage;
